const TZ = 'America/Los_Angeles';

export const DEFAULT_SESSION_WINDOWS = {
  morningStart: { hour: 6, minute: 50 },
  morningEnd: { hour: 7, minute: 59 },
  afternoonStart: { hour: 10, minute: 50 },
  afternoonEnd: { hour: 11, minute: 59 },
  biasMorningEnd: { hour: 7, minute: 20 },
  biasAfternoonEnd: { hour: 11, minute: 20 },
};

export function parseClock(value, fallback = { hour: 0, minute: 0 }) {
  if (value && typeof value === 'object' && Number.isFinite(value.hour) && Number.isFinite(value.minute)) {
    return { hour: value.hour, minute: value.minute };
  }
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return fallback;
  return { hour, minute };
}

export function formatClock(value) {
  const clock = parseClock(value, { hour: 0, minute: 0 });
  return `${String(clock.hour).padStart(2, '0')}:${String(clock.minute).padStart(2, '0')}`;
}

export function sessionWindowsFromParams(params = {}) {
  return {
    morningStart: parseClock(params.morningStart, DEFAULT_SESSION_WINDOWS.morningStart),
    morningEnd: parseClock(params.morningEnd, DEFAULT_SESSION_WINDOWS.morningEnd),
    afternoonStart: parseClock(params.afternoonStart, DEFAULT_SESSION_WINDOWS.afternoonStart),
    afternoonEnd: parseClock(params.afternoonEnd, DEFAULT_SESSION_WINDOWS.afternoonEnd),
    biasMorningEnd: parseClock(params.biasMorningEnd, DEFAULT_SESSION_WINDOWS.biasMorningEnd),
    biasAfternoonEnd: parseClock(params.biasAfternoonEnd, DEFAULT_SESSION_WINDOWS.biasAfternoonEnd),
  };
}

function addMinutes(clock, extra) {
  const total = ((clock.hour * 60 + clock.minute + extra) % (24 * 60) + 24 * 60) % (24 * 60);
  return { hour: Math.floor(total / 60), minute: total % 60 };
}

function clocksEqual(clock, hour, minute) {
  return clock.hour === hour && clock.minute === minute;
}

function zonedParts(input, timeZone = TZ) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
  };
}

export function sessionFlagsFromParts(hour, minute, options = {}) {
  const windows = { ...DEFAULT_SESSION_WINDOWS, ...sessionWindowsFromParams(options) };
  const {
    morningStart,
    morningEnd,
    afternoonStart,
    afternoonEnd,
    biasMorningEnd,
    biasAfternoonEnd,
  } = windows;
  const hm = hour * 60 + minute;
  const inRange = (start, end) => hm >= start.hour * 60 + start.minute && hm <= end.hour * 60 + end.minute;
  const isMorningSession = inRange(morningStart, morningEnd);
  const isAfternoonSession = inRange(afternoonStart, afternoonEnd);
  const determiningBias =
    inRange(morningStart, biasMorningEnd) || inRange(afternoonStart, biasAfternoonEnd);
  const endMorning = addMinutes(morningEnd, 2);
  const endAfternoon = addMinutes(afternoonEnd, 2);
  const endOfSession =
    clocksEqual(endMorning, hour, minute) || clocksEqual(endAfternoon, hour, minute);
  return {
    hour,
    minute,
    isMorningSession,
    isAfternoonSession,
    isTradingSession: isMorningSession || isAfternoonSession,
    determiningBias,
    endOfSession,
    sessionName: isMorningSession ? 'morning' : isAfternoonSession ? 'afternoon' : null,
  };
}

export function sessionAt(bar, options = {}) {
  const parts = zonedParts(bar?.timestamp || bar?.timeSec * 1000, options.timeZone || TZ);
  if (!parts) {
    return {
      hour: null,
      minute: null,
      isMorningSession: false,
      isAfternoonSession: false,
      isTradingSession: false,
      determiningBias: false,
      endOfSession: false,
      sessionName: null,
      dateKey: null,
      sessionKey: null,
    };
  }
  const flags = sessionFlagsFromParts(parts.hour, parts.minute, options);
  const dateKey = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
  return {
    ...flags,
    dateKey,
    sessionKey: flags.sessionName ? `${dateKey}-${flags.sessionName}` : null,
  };
}

export function sessionSeries(bars, options = {}) {
  return (bars || []).map((bar) => sessionAt(bar, options));
}

export function sessionRangeSeries(bars, sessionFlags) {
  const rangeHigh = new Array((bars || []).length).fill(null);
  const rangeLow = new Array((bars || []).length).fill(null);
  let key;
  let hi = null;
  let lo = null;
  for (let i = 0; i < (bars || []).length; i++) {
    const sk = sessionFlags?.[i]?.sessionKey ?? null;
    if (sk !== key) {
      key = sk;
      hi = null;
      lo = null;
    }
    if (sessionFlags?.[i]?.determiningBias) {
      const bar = bars[i];
      hi = hi == null ? bar.high : Math.max(hi, bar.high);
      lo = lo == null ? bar.low : Math.min(lo, bar.low);
    }
    rangeHigh[i] = hi;
    rangeLow[i] = lo;
  }
  return { rangeHigh, rangeLow };
}

export function sessionBoxes(bars, sessionFlags) {
  const boxes = [];
  let current = null;
  for (let i = 0; i < (bars || []).length; i++) {
    const key = sessionFlags?.[i]?.sessionKey ?? null;
    if (!key) {
      if (current) {
        boxes.push(current);
        current = null;
      }
      continue;
    }
    const bar = bars[i];
    if (!current || current.sessionKey !== key) {
      if (current) boxes.push(current);
      current = {
        sessionKey: key,
        startIndex: i,
        endIndex: i,
        high: bar.high,
        low: bar.low,
      };
      continue;
    }
    current.endIndex = i;
    current.high = Math.max(current.high, bar.high);
    current.low = Math.min(current.low, bar.low);
  }
  if (current) boxes.push(current);
  return boxes;
}
